import { useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiPost } from "../api/client";
import {
  useSetRecurrence,
  type RecurrenceFrequency,
  type RecurrenceConfig,
} from "../hooks/useRoadmap";

interface Props {
  workspaceId: string;
  /** null when the play has no roadmap_entry_id yet — the modal will
   *  POST to /make-recurring which creates the entry retroactively. */
  entryId: string | null;
  playId: string;
  playTitle: string;
  onClose: () => void;
}

/**
 * Recurrence picker for a play. Mirrors the inbox RecurrenceModal but
 * lives on the play detail page so users don't have to bounce back to
 * the inbox to schedule recurrence after a play exists. Both surfaces
 * write to the same PATCH /roadmap/:entryId/recurrence endpoint via
 * useSetRecurrence — the schedule lives on the roadmap entry, not
 * the play.
 */
export function PlayRecurrenceModal({ workspaceId, entryId, playId, playTitle, onClose }: Props) {
  const [frequency, setFrequency] = useState<RecurrenceFrequency>("monthly");
  const [dayOfMonth, setDayOfMonth] = useState("1");
  const [dayOfWeek, setDayOfWeek] = useState("1");
  const [startsOn, setStartsOn] = useState("");
  const qc = useQueryClient();
  // entryId path: existing PATCH endpoint via shared hook.
  const setRecurrence = useSetRecurrence(workspaceId);
  // No-entry path: POST /make-recurring promotes the play (creates entry
  // + links + sets recurrence in one atomic call).
  const makeRecurring = useMutation({
    mutationFn: (recurrence: RecurrenceConfig) =>
      apiPost(
        `/api/workspaces/${workspaceId}/plays/${playId}/make-recurring`,
        recurrence,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["play", playId] });
      qc.invalidateQueries({ queryKey: ["roadmap", workspaceId] });
      onClose();
    },
  });

  const showDayOfWeek = frequency === "weekly";
  const showDayOfMonth =
    frequency === "monthly" || frequency === "quarterly" || frequency === "annually";

  const handleSubmit = () => {
    const payload: RecurrenceConfig = { frequency };
    if (showDayOfMonth) {
      const n = Number(dayOfMonth);
      if (n >= 1 && n <= 28) payload.day_of_month = n;
    }
    if (showDayOfWeek) {
      const n = Number(dayOfWeek);
      if (n >= 0 && n <= 6) payload.day_of_week = n;
    }
    if (startsOn) payload.starts_on = startsOn;
    if (entryId) {
      setRecurrence.mutate(
        { entryId, recurrence: payload },
        { onSuccess: () => onClose() },
      );
    } else {
      makeRecurring.mutate(payload);
    }
  };

  const isPending = setRecurrence.isPending || makeRecurring.isPending;
  const error = setRecurrence.error?.message || makeRecurring.error?.message || null;

  const modal = (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.45)", display: "flex",
        alignItems: "center", justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-label="Make recurring"
        data-testid="play-recurrence-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--surface, #fff)", borderRadius: 12,
          width: 480, maxHeight: "85vh", display: "flex",
          flexDirection: "column", boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
        }}
      >
        <div style={{ padding: "18px 20px 14px", borderBottom: "1px solid var(--border)" }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-3)", marginBottom: 4 }}>
            Recurrence
          </div>
          <div style={{ fontWeight: 600, fontSize: 15, color: "var(--text-1)" }}>
            Make “{playTitle}” recurring
          </div>
          <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 4, lineHeight: 1.5 }}>
            The roadmap entry behind this play will be marked as a recurrence template. Each cycle will spawn a fresh play once the v1.5 scheduler is live.
          </div>
        </div>

        <div style={{ padding: "16px 20px", flex: 1, overflowY: "auto" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Frequency</span>
              <select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value as RecurrenceFrequency)}
                data-testid="play-recurrence-frequency"
                style={{ border: "1px solid var(--border)", borderRadius: 6, padding: 8, font: "inherit" }}
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="annually">Annually</option>
              </select>
            </label>
            {showDayOfWeek ? (
              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Day of week</span>
                <select
                  value={dayOfWeek}
                  onChange={(e) => setDayOfWeek(e.target.value)}
                  style={{ border: "1px solid var(--border)", borderRadius: 6, padding: 8, font: "inherit" }}
                >
                  <option value="1">Monday</option>
                  <option value="2">Tuesday</option>
                  <option value="3">Wednesday</option>
                  <option value="4">Thursday</option>
                  <option value="5">Friday</option>
                  <option value="6">Saturday</option>
                  <option value="0">Sunday</option>
                </select>
              </label>
            ) : null}
            {showDayOfMonth ? (
              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Day of month (1–28)</span>
                <input
                  type="number"
                  min={1}
                  max={28}
                  value={dayOfMonth}
                  onChange={(e) => setDayOfMonth(e.target.value)}
                  style={{ border: "1px solid var(--border)", borderRadius: 6, padding: 8, font: "inherit" }}
                />
              </label>
            ) : null}
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Starts on (optional)</span>
              <input
                type="date"
                value={startsOn}
                onChange={(e) => setStartsOn(e.target.value)}
                style={{ border: "1px solid var(--border)", borderRadius: 6, padding: 8, font: "inherit" }}
              />
            </label>
            {error ? (
              <div style={{ color: "crimson", fontSize: 12 }}>{error}</div>
            ) : null}
          </div>
        </div>

        <div style={{ padding: "12px 20px", borderTop: "1px solid var(--border)", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn primary"
            onClick={handleSubmit}
            disabled={isPending}
            data-testid="play-recurrence-submit"
          >
            {isPending ? "Saving…" : "Make recurring"}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
